/**
 * Parsed PDFs shared between the previews of one document, and quote lookup over their text.
 *
 * A preview acquires a document for as long as it is mounted. Up to `MAX_DOCUMENTS` stay
 * parsed; past that, the least recently acquired documents that no preview holds are
 * destroyed. A held document is never destroyed, so the cache can run over the limit while
 * more documents than that are on screen.
 */
import {pdfjs} from "../../../components/PreviewFile/Pdf/pdfjs";
import {buildPageTextIndex, findQuoteAcrossPages, findQuoteInPageIndex} from "./textMatching";
import type {PageTextIndex, ResolvedQuoteMatch} from "./types";

type PDFDocumentLoadingTask = ReturnType<typeof pdfjs.getDocument>;
export type PDFDocumentProxy = Awaited<PDFDocumentLoadingTask["promise"]>;

const MAX_DOCUMENTS = 8;

interface Entry {
  task: PDFDocumentLoadingTask;
  users: number;
}

/** Least recently acquired first: `Map` iterates in insertion order and an acquire re-inserts. */
const documents = new Map<string, Entry>();

const evictIdle = () => {
  for (const [url, entry] of documents) {
    if (documents.size <= MAX_DOCUMENTS) return;
    if (entry.users > 0) continue;
    documents.delete(url);
    void entry.task.destroy();
  }
};

export interface PdfHandle {
  promise: Promise<PDFDocumentProxy>;
  /** Idempotent. */
  release: () => void;
}

/** A document that failed to load is forgotten, so the next acquire retries it. */
export const acquirePdf = (url: string): PdfHandle => {
  let entry = documents.get(url);
  if (entry) {
    documents.delete(url);
  } else {
    const created: Entry = {task: pdfjs.getDocument(url), users: 0};
    created.task.promise.catch(() => {
      if (documents.get(url) === created) documents.delete(url);
    });
    entry = created;
  }
  documents.set(url, entry);
  entry.users++;
  evictIdle();

  const held = entry;
  let released = false;
  return {
    promise: held.task.promise,
    release: () => {
      if (released) return;
      released = true;
      held.users--;
      evictIdle();
    },
  };
};

const pageIndicesByDoc = new WeakMap<PDFDocumentProxy, Map<number, Promise<PageTextIndex>>>();

const pageIndex = (doc: PDFDocumentProxy, pageNumber: number): Promise<PageTextIndex> => {
  let pages = pageIndicesByDoc.get(doc);
  if (!pages) {
    pages = new Map();
    pageIndicesByDoc.set(doc, pages);
  }
  let index = pages.get(pageNumber);
  if (!index) {
    const building = (async () => {
      const page = await doc.getPage(pageNumber);
      const {items} = await page.getTextContent();
      return buildPageTextIndex(pageNumber, items as Array<{str: string}>);
    })();
    const cached = pages;
    building.catch(() => cached.delete(pageNumber));
    pages.set(pageNumber, building);
    index = building;
  }
  return index;
};

/**
 * Where a quote sits in a document, read from its text content without rendering. With `page`,
 * only that page and the page breaks either side of it are searched; otherwise pages are
 * searched in order until the first match.
 */
export const locateQuote = async (
  doc: PDFDocumentProxy,
  quote: string,
  page?: number,
): Promise<ResolvedQuoteMatch | null> => {
  if (page != null) {
    const here = await pageIndex(doc, page);
    const onPage = findQuoteInPageIndex(quote, here);
    if (onPage) return onPage;
    if (page < doc.numPages) {
      const across = findQuoteAcrossPages(quote, here, await pageIndex(doc, page + 1));
      if (across) return across;
    }
    if (page > 1) return findQuoteAcrossPages(quote, await pageIndex(doc, page - 1), here);
    return null;
  }

  let previous: PageTextIndex | null = null;
  for (let n = 1; n <= doc.numPages; n++) {
    const index = await pageIndex(doc, n);
    const match = findQuoteInPageIndex(quote, index) ?? (previous && findQuoteAcrossPages(quote, previous, index));
    if (match) return match;
    previous = index;
  }
  return null;
};
